/**
 * The economy's two headline questions: **what does this resolution intent cost?**
 * ({@link intentCost}) and **may the actor afford it right now?** ({@link checkBudget}).
 */

import type { EarmarkVerb, EncounterState } from "@lancer-vtt/domain";
import { bracedTurnLimit, canUseReaction, reactionSlotOpen, type BracedTurnLimit } from "@lancer-vtt/domain";
import type { EncounterIntent, RequestActivate } from "@lancer-vtt/shared";
import { ok, err } from "@lancer-vtt/shared";
import { gatherActions, type LoadedOverlay } from "../effects/loader";
import { actionCostOverride, SEED_OVERLAY } from "../effects/seed";
import { namesDestroyedWeapon, namesUnknownWeapon, selectWeapon } from "../resolve";
import { frozenTechActivation } from "./actionShapes";
import { earmarkedVerbHeld } from "./actionId";
import { OVERWATCH_REACTION, reactionShotGrant } from "./reactionShot";
import { CORE_ACTION_CATALOG, type CoreAction } from "../coreActionCatalog";
import type { BudgetVerdict } from "./types";
import { affordable } from "../events/gameEvents";

/** The action-economy cost of a resolution intent. */
export type IntentCost = {
  /** The combatant whose budget this intent draws on (the attacker / activating actor). */
  actor: string;
  /** Action **points** spent (Quick=1, Full=2; 0 for a reaction / free / protocol). */
  points: number;
  /** `true` iff this intent is a **reaction** (spends the global reaction slot, not a point). */
  reaction: boolean;
  /**
   * The NAMED reaction id (`"overwatch"`, `"brace"`, or an overlay reaction's activated id) this intent spends, when `reaction` is
   * true — `checkBudget` gates it against the round-scoped named cap alongside the global slot.
   * `undefined` for a non-reaction cost (and, defensively, for a reaction whose id isn't yet
   * plumbed through).
   */
  reactionId?: string;
  /** The activation label resolved (for the play-by-play / diagnostics). */
  label: string;
  /**
   * The RAW verb whose held **cost earmark** (`grant_free_action_use`) zeroed this intent's
   * `points` — set ONLY when an earmark was actually found and applied, so it is
   * both the "this was free because of an earmark" flag and the key the consume step must
   * decrement. `undefined` on every ordinary cost, including a genuinely-free one (a protocol,
   * a reaction): a zero `points` does NOT imply an earmark, and a consumer must branch on this
   * field rather than on `points === 0` or it will burn an earmark nobody held.
   *
   * The caller that ACCEPTS the intent owes the matching consume (`refereeIntent.ts`'s sibling
   * fold). Pricing alone never mutates state — this type is pure.
   */
  freeUseVerb?: EarmarkVerb;
  /**
   * `true` iff this intent is a RAW **free action** (Overcharge, a Protocol, a granted `free`
   * action, releasing a grapple). Distinct from `points === 0`: a lifecycle step (`end_attack`), a
   * reaction, a mid-attack continuation and a GM stamp also cost nothing and are not free actions.
   * Read by the braced-turn wall in {@link checkBudget}.
   */
  freeAction?: true;
}

/** Map an overlay `activation` to its action-point cost (a reaction/free/protocol = 0). */
function pointsForActivation(activation: ActionActivation): number {
  switch (activation) {
    case "full":
      return 2;
    case "quick":
    case "quick_tech":
      return 1;
    case "reaction":
    case "protocol":
    case "free":
      return 0;
  }
}

type ActionActivation = "quick" | "quick_tech" | "full" | "protocol" | "reaction" | "free";

/**
 * The action-economy cost of `intent`. An attack is a **Quick** action (1 point) — the faithful
 * default for a weapon attack (a per-weapon Quick/Full refinement is a loadout follow-up). An
 * activation costs by its overlay `activation`: the highest-cost matching action wins (an entity
 * declaring several actions is gated by its most expensive). An activation that resolves no
 * overlay action falls back to a free (0-point) cost — the resolver itself will reject an unknown
 * action, so the economy stays permissive there and lets the resolver own that error.
 *
 * `state` is **optional** — it exists solely so the `request_attack` branch can
 * look up the attacker's `kind` and charge a pilot's weapon attack (Fight, RAW p.75) as a FULL
 * action instead of the mech default's Quick. A caller that can't supply `state` degrades to the
 * mech-default Quick cost, never a crash.
 */
export function intentCost(
  intent: EncounterIntent,
  overlay: LoadedOverlay = SEED_OVERLAY,
  state?: EncounterState,
): IntentCost {
  const base = baseIntentCost(intent, overlay, state);
  // ── The cost-earmark discount ──
  // Applied HERE, as a wrapper over the whole priced result, rather than inside any individual
  // branch: `baseIntentCost` has ~25 return sites, and a discount threaded through them would be
  // one forgotten branch away from a verb that silently still charges. One chokepoint means a
  // new intent branch inherits the earmark for free and cannot opt out by omission.
  //
  // A reaction is deliberately NOT discountable: it spends the global reaction slot, not action
  // points, so zeroing `points` would consume an earmark and change nothing observable.
  if (base.points <= 0 || base.reaction) return base;
  const verb = earmarkedVerbHeld(intent, state, base.actor);
  if (verb === undefined) return base;
  return { ...base, points: 0, label: `free (${verb} — earmarked)`, freeUseVerb: verb };
}

/**
 * Action points per core-catalog `cost`, mirroring {@link pointsForActivation}. A `Reaction` row
 * costs 0 points and marks the cost a reaction.
 */
const CATALOG_POINTS = { Quick: 1, Full: 2, Reaction: 0, Free: 0, Protocol: 0 } as const satisfies Record<CoreAction["cost"], number>;

/**
 * Bare core-action id → its RAW `cost`, read off {@link CORE_ACTION_CATALOG}. Keyed on the BARE id
 * with no provenance, which is sound only because every id shared by two or more of the three
 * tables agrees on `cost` (pinned in `economy.test.ts`). That is what lets {@link intentCost} keep
 * `state` optional.
 */
export const CORE_ACTION_COST: ReadonlyMap<string, CoreAction["cost"]> = new Map(
  [...CORE_ACTION_CATALOG].map(([composite, action]) => [composite.slice(composite.indexOf(":") + 1), action.cost]),
);

/** A catalog-priced verb: its bare core-action id, and the name its cost label prints. */
type CatalogPricing = { id: string; name: string };

/**
 * How each {@link EncounterIntent} member is priced. A `CatalogPricing` row reads its points off
 * the RAW core-action catalog; `"hand"` means a hand-written arm in {@link baseIntentCost} prices
 * it, each with its one-line reason there. The `satisfies` makes a new intent member a compile
 * error here instead of a silent fall-through into the overlay-activation branch.
 *
 * This is the SECOND intent → action-id mapping, beside `actionIdForIntent` (`./actionId.ts`),
 * which is the once-per-turn LEDGER id: it returns `null` for "no ledger entry", not "free", and
 * spells several ids `act_*`. `projects/action-id-vocabulary.md` owns reconciling the two.
 */
export const INTENT_PRICING = {
  request_barrage: { id: "barrage", name: "barrage" },
  request_stabilize: { id: "stabilize", name: "stabilize" },
  request_overcharge: { id: "overcharge", name: "overcharge" },
  request_full_tech: { id: "full_tech", name: "full tech" },
  request_hide: { id: "hide", name: "hide" },
  request_disengage: { id: "disengage", name: "disengage" },
  request_shutdown: { id: "shut_down", name: "shut down" },
  request_engage_compcon: { id: "engage_compcon", name: "engage compcon" },
  request_take_back_compcon: { id: "take_back_compcon", name: "take back from compcon" },
  request_boot_up: { id: "boot_up", name: "boot up" },
  request_self_destruct: { id: "self_destruct", name: "self-destruct" },
  request_dismount: { id: "dismount", name: "dismount" },
  request_eject: { id: "eject", name: "eject" },
  request_reload: { id: "reload", name: "reload" },
  request_mount: { id: "mount", name: "mount" },
  request_search: { id: "search", name: "search" },
  request_jockey: { id: "jockey", name: "jockey" },
  request_ride: { id: "ride", name: "ride" },
  request_ride_dismount: { id: "ride_dismount", name: "ride dismount" },
  request_attack: "hand",
  gm_force: "hand",
  end_attack: "hand",
  stop_barrage_as_skirmish: "hand",
  request_grapple_break: "hand",
  request_grapple_release: "hand",
  request_grapple_contest: "hand",
  request_jockey_menu: "hand",
  request_shake_off: "hand",
  request_deploy_drone: "hand",
  request_deploy_mine: "hand",
  request_detect_mine: "hand",
  request_disarm_mine: "hand",
  request_detonate_mine: "hand",
  request_activate: "hand",
  request_move: "hand",
  request_end_turn: "hand",
  request_gm_damage: "hand",
} as const satisfies Record<EncounterIntent["type"], CatalogPricing | "hand">;

type CatalogPricedType = {
  [K in keyof typeof INTENT_PRICING]: (typeof INTENT_PRICING)[K] extends CatalogPricing ? K : never;
}[keyof typeof INTENT_PRICING];

/** A type predicate, so the hand-written arms after the catalog lookup narrow past the catalog-priced members. */
function isCatalogPriced(intent: EncounterIntent): intent is Extract<EncounterIntent, { type: CatalogPricedType }> {
  return INTENT_PRICING[intent.type] !== "hand";
}

/** The cost of core action `id` for `actor`, labelled `"<cost> (<name>)"` — e.g. `"quick (hide)"`. */
function catalogCost(actor: string, pricing: CatalogPricing): IntentCost {
  const cost = CORE_ACTION_COST.get(pricing.id);
  if (cost === undefined) throw new Error(`intentCost: no core-action catalog row "${pricing.id}"`);
  const freeAction = cost === "Free" || cost === "Protocol";
  return { actor, points: CATALOG_POINTS[cost], reaction: cost === "Reaction", label: `${cost.toLowerCase()} (${pricing.name})`, ...(freeAction ? { freeAction } : {}) };
}

/**
 * `intentCost`'s undiscounted body — the RAW action-economy price of `intent` before any held
 * cost earmark is applied. Split out so the earmark discount has exactly ONE application site
 * (see {@link intentCost}).
 *
 * A core verb with a catalog row is priced off {@link CORE_ACTION_CATALOG} via {@link INTENT_PRICING}.
 * The attack pair stays hand-ordered because its precedence (reaction → improvised → pilot-kind →
 * full tech → default) is not `actionIdForAttackLike`'s; within it the catalog still supplies
 * every points value except Overwatch and Sidearm. The rest are the arms that cannot derive: not a
 * core-catalog row, a GM stamp that deliberately drops the budget, or a different data source.
 */
function baseIntentCost(
  intent: EncounterIntent,
  overlay: LoadedOverlay = SEED_OVERLAY,
  state?: EncounterState,
): IntentCost {
  if (isCatalogPriced(intent)) {
    const actor = intent.type === "request_barrage" ? intent.attacker : intent.actor;
    const pricing = INTENT_PRICING[intent.type];
    const priced = catalogCost(actor, pricing);
    // A standing re-price the actor holds (Black Thumb 1's Quick Dismount): the referee's spend
    // reads this same cost, so the gate and the spend agree.
    const override = state === undefined ? undefined : actionCostOverride({ state, actorId: actor, action: pricing.id, overlay });
    if (override === undefined) return priced;
    return { ...priced, points: pointsForActivation(override.activation), label: `${override.activation} (${pricing.name} — ${override.source})` };
  }
  // A forced action costs what the action it wraps costs (`decisions/0209`); the forced spend
  // policy decides whether it is paid.
  if (intent.type === "gm_force") return baseIntentCost(intent.intent, overlay, state);
  if (intent.type === "request_attack") {
    // Overwatch spends the global reaction slot, not points, and is capped by its own named
    // 1/round `reactionId`. A shot a rule grants (Covering Fire, Impending Threat, Moving Target:
    // `economy/reactionShot.ts`) is that rule's own reaction, so it spends its own key and leaves
    // Overwatch untouched.
    if (intent.reaction) {
      const grant = state !== undefined ? reactionShotGrant(state, intent) : undefined;
      if (grant !== undefined) {
        return { actor: intent.attacker, points: 0, reaction: true, reactionId: grant.reactionId, label: `reaction (${grant.verb})` };
      }
      return { actor: intent.attacker, points: 0, reaction: true, reactionId: OVERWATCH_REACTION, label: "reaction (overwatch)" };
    }
    // Improvised Attack (Core p.71) is the one melee action attack that's a Full action.
    if (intent.weapon === "act_improvised_attack") {
      return catalogCost(intent.attacker, { id: "improvised_attack", name: "improvised attack" });
    }
    // A `pilot`-kind attacker's weapon attack IS Fight (Core p.75), unless the SELECTED weapon
    // carries Sidearm, which makes Fight Quick — the tag changes Fight's own cost, so it reads off
    // the weapon `selectWeapon` would fire, and has no catalog row.
    if (state?.combatants[intent.attacker]?.kind === "pilot") {
      const atk = state.combatants[intent.attacker]!;
      // Priced before the room's `checkAttackWeapon` wall runs, so an unknown or destroyed weapon ref
      // (or a tech attack's action id, which fires no weapon) reads no weapon rather than throwing.
      const w = intent.tech || namesUnknownWeapon(atk, intent.weapon) || namesDestroyedWeapon(atk, intent.weapon) ? undefined : selectWeapon(atk, intent.weapon, intent.profile);
      if (w?.sidearm) return { actor: intent.attacker, points: 1, reaction: false, label: "quick (fight — sidearm)" };
      return catalogCost(intent.attacker, { id: "fight", name: "fight" });
    }
    // A Full Tech system-granted tech attack (Markerlight and its family, Core p.71). Reads the
    // SAME frozen activation `actionIdForAttackLike` derives its ledger id off, so the cost and the
    // ledger id can never disagree.
    if (intent.tech && frozenTechActivation(state, intent.attacker, intent.weapon) === "full_tech") {
      return catalogCost(intent.attacker, { id: "full_tech", name: "full tech" });
    }
    return catalogCost(intent.attacker, { id: "skirmish", name: "attack" });
  }
  // ── Hand-priced: no core-catalog row, a GM stamp, or a lifecycle step ──
  // Lifecycle, not an action: the action was charged on the opener; this is a free lock-close.
  if (intent.type === "end_attack") return { actor: intent.attacker, points: 0, reaction: false, label: "free (end attack)" };
  // Lifecycle: converts the open Barrage lock to a Skirmish; the Barrage was charged on the opener.
  // Only its `reaction` flag is ever read (the activation fold), and it has no overlay action.
  if (intent.type === "stop_barrage_as_skirmish") return { actor: intent.attacker, points: 0, reaction: false, label: "free (no action)" };
  // No catalog row: `grapple` is the attack, not the defender's contested break (Core p.72, Quick).
  if (intent.type === "request_grapple_break") return { actor: intent.actor, points: 1, reaction: false, label: "quick (grapple break)" };
  // No catalog row: RAW "the attacker chooses to end the grapple as a FREE action" (Core p.72).
  // Still turn-gated — `checkBudget`'s turn gate applies regardless of points.
  if (intent.type === "request_grapple_release") return { actor: intent.actor, points: 0, reaction: false, label: "free (grapple release)", freeAction: true };
  // No catalog row: RAW names no point cost for the same-Size turn-start tiebreak (Core p.69).
  if (intent.type === "request_grapple_contest") return { actor: intent.actor, points: 0, reaction: false, label: "free (grapple contest)" };
  // No catalog row: the repeatable Jockey menu is a Full action each subsequent turn (Core p.75).
  if (intent.type === "request_jockey_menu") return { actor: intent.actor, points: 2, reaction: false, label: "full (jockey menu)" };
  // No catalog row: the carrier's shake-off is a Full action (Core p.75), dispatched by the mech.
  if (intent.type === "request_shake_off") return { actor: intent.actor, points: 2, reaction: false, label: "full (shake off)" };
  // Tag-granted verbs (DRONE/MINE, Core pp.105-106), not core-catalog actions — all Quick.
  if (intent.type === "request_deploy_drone") return { actor: intent.actor, points: 1, reaction: false, label: "quick (deploy drone)" };
  if (intent.type === "request_deploy_mine") return { actor: intent.actor, points: 1, reaction: false, label: "quick (deploy mine)" };
  if (intent.type === "request_detect_mine") return { actor: intent.actor, points: 1, reaction: false, label: "quick (detect mine)" };
  if (intent.type === "request_disarm_mine") return { actor: intent.actor, points: 1, reaction: false, label: "quick (disarm mine)" };
  if (intent.type === "request_detonate_mine") return { actor: intent.actor, points: 1, reaction: false, label: "quick (detonate mine)" };
  // Routed off before pricing (`applyMoveIntent` / `applyEndTurnIntent` / the GM-damage referee),
  // so no caller charges these; priced free rather than cast into the overlay branch below.
  if (intent.type === "request_move" || intent.type === "request_end_turn") {
    return { actor: intent.combatantId, points: 0, reaction: false, label: "free (no action)" };
  }
  if (intent.type === "request_gm_damage") return { actor: "", points: 0, reaction: false, label: "free (no action)" };
  // A different data source by construction: priced off the loaded overlay's `activation`.
  return overlayActivationCost(intent, overlay);
}

/**
 * A `request_activate`'s cost by its overlay `activation`: the most expensive of the entity's
 * actions gates it, and a reaction among them marks the whole activation a reaction (it spends a
 * per-round reaction, not a point) with a named 1/round cap keyed by the activated id, unless the
 * clause is `frequency: "unlimited"`. No overlay action ⇒ free — the resolver will reject it.
 */
function overlayActivationCost(act: RequestActivate, overlay: LoadedOverlay): IntentCost {
  const actions = gatherActions({ ownerId: act.actor, entityId: act.action, overlay });
  if (actions.length === 0) {
    // No overlay action — the resolver will reject it; the economy is permissive (0).
    return { actor: act.actor, points: 0, reaction: false, label: "free (no action)" };
  }
  let points = 0;
  let reaction = false;
  let unlimited = false;
  let label: string = actions[0]!.effect.activation;
  for (const a of actions) {
    const activation = a.effect.activation as ActionActivation;
    const p = pointsForActivation(activation);
    if (activation === "reaction") {
      reaction = true;
      if (a.effect.frequency === "unlimited") unlimited = true;
    }
    if (p > points) {
      points = p;
      label = activation;
    }
  }
  if (reaction && points === 0) label = "reaction";
  const freeAction = !reaction && points === 0;
  // RAW reactions are 1/round unless they say otherwise; the cap is keyed by the activated id.
  return { actor: act.actor, points, reaction, label, ...(reaction && !unlimited ? { reactionId: act.action } : {}), ...(freeAction ? { freeAction } : {}) };
}

/**
 * May `cost.actor` afford `cost` against the encounter's current {@link TurnBudget}? The
 * enforced rules:
 *   - **No budget tracked** ⇒ allowed (inert).
 *   - A **non-reaction** must be the **active** combatant's (`budget.actor`); a
 *     non-active combatant acting on its own initiative is out-of-turn ⇒ rejected.
 *   - A non-reaction must have enough **action points** left (`points <= budget.actions`).
 *   - A **reaction** may be spent **out of turn** (any combatant), but only if BOTH (1) its
 *     GLOBAL reaction slot is open ({@link reactionSlotOpen} — one reaction per turn, reopened at
 *     every turn start) AND (2), when `cost.reactionId` is known, that
 *     NAMED reaction hasn't already been used since the reactor's own last turn start
 *     ({@link canUseReaction} — Core p.73, independent of how often the slot refreshes).
 *
 * Returns a verdict the room surfaces (a rejection is a logged no-op before any dice).
 */
export function checkBudget(state: EncounterState, cost: IntentCost): BudgetVerdict {
  const turn = state.activation.turn;
  // No turn has been sequenced yet (`turn.phase === "idle"` — the fight hasn't started; the
  // GM is still setting up, no `activate` has bound an acting combatant). Enforcement is
  // inert until the first turn begins; the economy only governs a *running* fight. This is
  // the faithful reading (Lancer's action economy applies during structured turns) AND what
  // keeps GM adjudication and un-sequenced authoring working before a fight is sequenced.
  //
  // It does NOT exist to keep a "deploy-then-resolve setup/pipeline path" open — an earlier
  // version of this comment claimed that, and no such path exists (the claim's twin in
  // `ARCHITECTURE.md` has been retracted by name). Note also what this bypass is not: it is
  // not a caller-role decision. `checkBudget` is pure over `EncounterState` and has no idea
  // who is calling. Deciding whether a GM or a player may act on an un-sequenced encounter
  // lives at the room, in `activationDisposition`, per `projects/pre-activation-affordances.md` D2.
  if (turn.phase !== "acting") return ok(undefined);

  if (cost.reaction) {
    if (!affordable({ kind: "action_points", carrier: cost.actor, points: 0, reaction: {} }, state, undefined)) {
      return err(`${cost.actor} has no reaction available right now (one reaction per turn; it reopens when the next turn starts) (${cost.label})`);
    }
    if (cost.reactionId !== undefined && !affordable({ kind: "action_points", carrier: cost.actor, points: 0, reaction: { name: cost.reactionId } }, state, undefined)) {
      return err(`${cost.actor} has already used ${cost.reactionId} (it refreshes when its own next turn starts) (${cost.label})`);
    }
    return ok(undefined);
  }

  // A standard/quick/full action: must be the acting combatant's turn.
  if (turn.actor !== cost.actor) {
    return err(
      `out of turn — it is ${turn.actor}'s turn, not ${cost.actor}'s (${cost.label})`,
    );
  }
  const braced = bracedTurnRefusal(state, cost);
  if (braced !== undefined) return err(braced);
  if (!affordable({ kind: "action_points", carrier: cost.actor, points: cost.points }, state, undefined)) {
    return err(
      `over budget — ${cost.actor} has ${turn.budget.actions} action point(s) left, needs ${cost.points} (${cost.label})`,
    );
  }
  return ok(undefined);
}

/**
 * Brace's price on the bracer's next turn (RAW Core p.73: "on that turn, you can only take one quick
 * action – you cannot OVERCHARGE, move normally, take full actions, or take free actions"), as the
 * refusal reason, or `undefined` when `cost` is legal. The move half is the `move_token` wall and
 * Boost's (`@lancer-vtt/domain`'s `onBracedTurn`); the no-reactions half is `Braced` itself
 * (`checkReactionStatusLegality`). A cost that is not an action at all (a lifecycle step, a
 * mid-attack continuation) passes.
 */
function bracedTurnRefusal(state: EncounterState, cost: IntentCost): string | undefined {
  const actor = state.combatants[cost.actor];
  const action = cost.freeAction ? "free" : cost.points >= 2 ? "full" : cost.points === 1 || cost.freeUseVerb !== undefined ? "quick" : undefined;
  if (!actor || action === undefined) return undefined;
  const limit = bracedTurnLimit(actor, state.activation, action);
  if (limit === undefined) return undefined;
  const why = `${actor.name} Braced last turn, so this turn it can take only one Quick action (RAW p.73)`;
  return `${why} — ${BRACED_LIMIT_WORDS[limit]} (${cost.label})`;
}

/** The tail of a braced-turn refusal, per {@link BracedTurnLimit}. */
const BRACED_LIMIT_WORDS: Record<BracedTurnLimit, string> = {
  no_free: "no free actions, Protocols or Overcharge",
  no_full: "no Full actions",
  quick_taken: "it has already taken it",
};

